import { getApiBaseUrl } from './appConfig';

const normalizeRole = (value) => String(value || '').trim().toLowerCase();

const normalizeUsername = (value) => String(value || '').trim().toLowerCase();

export const BLOCKED_USER_MESSAGE =
  'This account has been blocked. Please contact your administrator.';

const isTruthyBlocked = (value) =>
  value === true || value === 1 || String(value).toLowerCase() === 'true';

/** True when login payload or API message indicates a blocked account. */
export function isUserBlockedFromLogin(data, loginUsername = '') {
  const payload = normalizeLoginPayload(data, loginUsername);

  if (isTruthyBlocked(payload.isBlocked ?? payload.IsBlocked)) return true;

  const nested =
    payload.user ?? payload.User ?? payload.data?.user ?? payload.data?.User;
  if (nested && isTruthyBlocked(nested.IsBlocked ?? nested.isBlocked)) return true;

  const msg = String(
    payload.message ?? payload.Message ?? data?.message ?? data?.Message ?? ''
  ).toLowerCase();
  if (msg.includes('block')) return true;

  return false;
}

export function isBlockedUserErrorMessage(message) {
  return String(message || '').toLowerCase().includes('block');
}

/** Fallback when login response omits IsBlocked — verify via User/GetById. */
export async function isUserBlockedById(token, userId) {
  if (!token || token === 'authenticated' || !userId) return false;

  try {
    const baseUrl = getApiBaseUrl();
    const res = await fetch(`${baseUrl}/User/GetById/${userId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    const body = await res.json().catch(() => null);
    const user = body?.data ?? body;
    return isTruthyBlocked(user?.IsBlocked ?? user?.isBlocked);
  } catch {
    return false;
  }
}

/** Unwrap { data: {...} } login envelopes from the API. */
export function normalizeLoginPayload(body, loginUsername = '') {
  const root = body?.data && typeof body.data === 'object' && !Array.isArray(body.data)
    ? { ...body, ...body.data }
    : body || {};

  const username =
    root.username ??
    root.Username ??
    loginUsername ??
    '';

  return {
    ...root,
    username,
    Username: root.Username ?? username,
  };
}

/** True when login/user payload is an Admin (storefront may redirect to /admin). */
export function isAdminUser(data, loginUsername = '') {
  const payload = normalizeLoginPayload(data, loginUsername);

  const roleName = normalizeRole(
    payload.roleName ?? payload.RoleName ?? payload.role ?? payload.Role ?? ''
  );

  if (roleName === 'admin') return true;
  if (payload.isAdmin === true || payload.IsAdmin === true) return true;

  const username = normalizeUsername(
    payload.username ?? payload.Username ?? loginUsername
  );
  if (username === 'admin') return true;

  return false;
}

export function isAdminRoleName(roleName) {
  return normalizeRole(roleName) === 'admin';
}

/** Can open /admin (role from login or stored username admin). */
export function canAccessAdminPanel({ roleName, username } = {}) {
  if (isAdminRoleName(roleName)) return true;
  if (normalizeUsername(username) === 'admin') return true;
  return false;
}

/** Profile greeting — username only (login field or API Username). */
export function getProfileDisplayName(data, loginUsername = '') {
  const typed = String(loginUsername || '').trim();
  if (typed) return typed;

  const payload = normalizeLoginPayload(data, loginUsername);
  const fromApi = String(payload.Username ?? payload.username ?? '').trim();
  if (fromApi) return fromApi;

  return 'User';
}

/** Read numeric user id from JWT when login body omits UserId. */
export function parseUserIdFromToken(token) {
  if (!token || token === 'authenticated') return null;
  try {
    const parts = String(token).split('.');
    if (parts.length < 2) return null;
    const json = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
    const payload = JSON.parse(json);
    const candidates = [
      payload.userId,
      payload.UserId,
      payload.sub,
      payload.nameid,
      payload.id,
      payload.Id,
      payload.unique_name,
    ];
    for (const value of candidates) {
      const num = Number(value);
      if (Number.isFinite(num) && num > 0) return num;
    }
  } catch {
    /* not a JWT or invalid payload */
  }
  return null;
}

/** UserId from login response or JWT token. */
export function resolveUserIdFromLogin(data, loginUsername = '') {
  const payload = normalizeLoginPayload(data, loginUsername);
  const candidates = [
    payload.userId,
    payload.UserId,
    payload.id,
    payload.Id,
    data?.userId,
    data?.UserId,
    data?.data?.userId,
    data?.data?.UserId,
  ];
  for (const value of candidates) {
    const num = Number(value);
    if (Number.isFinite(num) && num > 0) return num;
  }

  const token =
    payload.token ?? payload.Token ?? payload.accessToken ??
    data?.token ?? data?.Token ?? data?.accessToken;
  return parseUserIdFromToken(token);
}

/** Role string to store after login when API omits RoleName. */
export function resolveRoleNameFromLogin(data, loginUsername = '') {
  const payload = normalizeLoginPayload(data, loginUsername);
  const fromApi =
    payload.roleName ?? payload.RoleName ?? payload.role ?? payload.Role ?? '';
  if (String(fromApi).trim()) return String(fromApi).trim();

  const username = normalizeUsername(
    payload.username ?? payload.Username ?? loginUsername
  );
  if (username === 'admin') return 'Admin';

  return '';
}

const ADMIN_RETURN_URL_KEY = 'bw_admin_return_url';

export function saveAdminReturnUrl(path) {
  const value = String(path || '').trim();
  if (!value.startsWith('/admin')) return;
  sessionStorage.setItem(ADMIN_RETURN_URL_KEY, value);
}

export function consumeAdminReturnUrl() {
  const value = sessionStorage.getItem(ADMIN_RETURN_URL_KEY);
  sessionStorage.removeItem(ADMIN_RETURN_URL_KEY);
  if (value && value.startsWith('/admin')) return value;
  return '/admin';
}

export function clearAdminReturnUrl() {
  sessionStorage.removeItem(ADMIN_RETURN_URL_KEY);
}
